import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, ButtonGroup, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { trackPromise } from 'react-promise-tracker';
import Swal from 'sweetalert2';
import moment from 'moment';
import caAPI from '../../../../services/caAPI';
import ModalDommus from '../../../../components/Modal';
import Anexos from '../../../../components/Anexos';
import DommusToggle from '../../../../components/DommusToggle';
import DommusTabelaPaginadaControlada from '../../../../components/DommusTabelaPaginadaControlada';
import { errorToast, infoToast, successToast } from '../../../../components/DommusToast';
import { extrairDados, obterMensagemErro } from '../../GestaoAgentesIa/utils';
import ExemplosAprendizado from '../ExemplosAprendizado';

const EXTENSOES_PERMITIDAS = ['pdf', 'txt', 'md', 'docx'];
const MAXIMO_ARQUIVOS = 10;
const MAXIMO_ARQUIVO_BYTES = 26214400;
const MAXIMO_LOTE_BYTES = 104857600;
const MAXIMO_CARACTERES_ORIENTACAO = 1000;

const CAMPOS_ORIENTACAO = [
  { campo: 'descricao', rotulo: 'Descrição (administrativa)', ajuda: 'Uso interno, não é enviada ao Agente.' },
  { campo: 'descricao_conteudo', rotulo: 'Conteúdo disponível', ajuda: 'O que há nesta base de conhecimento.' },
  { campo: 'orientacoes_consulta', rotulo: 'Orientações de consulta', ajuda: 'Quando e como o Agente deve consultar esta base.' },
  { campo: 'regras_utilizacao', rotulo: 'Regras de utilização', ajuda: 'Como usar o que for recuperado.' },
  { campo: 'restricoes', rotulo: 'Restrições', ajuda: 'O que o Agente não deve fazer com esta base.' },
];

const ORIENTACAO_VAZIA = CAMPOS_ORIENTACAO.reduce((acumulado, { campo }) => ({ ...acumulado, [campo]: '' }), {});

function obterMensagemCampo(erro, campo, mensagemPadrao) {
  const mensagem = erro?.response?.data?.[campo];
  return Array.isArray(mensagem) ? mensagem[0] : mensagem || mensagemPadrao;
}

export default function ModalBaseConhecimento({ aberto, close, idBaseConhecimento, onSalvo, onAtualizarLista, chaveVectorStoreConfigurada }) {
  const [nome, setNome] = useState('');
  const [orientacao, setOrientacao] = useState(ORIENTACAO_VAZIA);
  const [consultaObrigatoria, setConsultaObrigatoria] = useState(false);
  const [baseAtiva, setBaseAtiva] = useState(true);
  const [paraAprendizado, setParaAprendizado] = useState(false);
  const [errosOrientacao, setErrosOrientacao] = useState({});
  const [erroNome, setErroNome] = useState('');
  const [erroGeral, setErroGeral] = useState('');
  const [salvandoBase, setSalvandoBase] = useState(false);
  const [listaArquivos, setListaArquivos] = useState([]);
  const [paginaArquivos, setPaginaArquivos] = useState(1);
  const [tamanhoPaginaArquivos, setTamanhoPaginaArquivos] = useState(15);
  const [totalArquivos, setTotalArquivos] = useState(0);
  const [arquivosSelecionados, setArquivosSelecionados] = useState([]);

  const editando = Boolean(idBaseConhecimento);
  const tooltipChaveNaoConfigurada = useMemo(() => (
    <Tooltip id="tooltip-chave-vector-store-nao-configurada-modal">
      Configure a chave de Vector Store antes de gerenciar Bases de Conhecimento.
    </Tooltip>
  ), []);

  const handleCarregarArquivos = useCallback((paginaSolicitada, tamanhoSolicitado = tamanhoPaginaArquivos) => {
    return trackPromise(
      caAPI.baseConhecimento.listarArquivos(idBaseConhecimento, {
        page: paginaSolicitada,
        per_page: tamanhoSolicitado,
      })
        .then(resposta => {
          setListaArquivos(extrairDados(resposta));
          setTotalArquivos(resposta?.data?.total || 0);
          setErroArquivos('');
        })
        .catch(erro => setErroArquivos(obterMensagemErro(erro, 'Erro ao carregar arquivos da Base.')))
    );
  }, [idBaseConhecimento, tamanhoPaginaArquivos]);

  const [erroArquivos, setErroArquivos] = useState('');

  useEffect(() => {
    if (!aberto) return;

    setErroNome('');
    setErroGeral('');
    setErrosOrientacao({});
    setArquivosSelecionados([]);
    setPaginaArquivos(1);
    if (!editando) {
      setNome('');
      setOrientacao(ORIENTACAO_VAZIA);
      setConsultaObrigatoria(false);
      setBaseAtiva(true);
      setParaAprendizado(false);
      setListaArquivos([]);
      setTotalArquivos(0);
      return;
    }

    trackPromise(
      caAPI.baseConhecimento.buscarPorId(idBaseConhecimento)
        .then(resposta => {
          const base = extrairDados(resposta) || {};
          setNome(base.nome || '');
          setOrientacao(CAMPOS_ORIENTACAO.reduce((acumulado, { campo }) => ({ ...acumulado, [campo]: base[campo] || '' }), {}));
          setConsultaObrigatoria(Boolean(base.consulta_obrigatoria));
          setBaseAtiva(base.ativo !== false);
          setParaAprendizado(Boolean(base.para_aprendizado));
        })
        .catch(erro => {
          setErroGeral(obterMensagemErro(erro, 'Erro ao carregar Base de Conhecimento.'));
        })
    );
  }, [aberto, editando, idBaseConhecimento]);

  useEffect(() => {
    if (!aberto || !editando) return;
    handleCarregarArquivos(paginaArquivos);
  }, [aberto, editando, handleCarregarArquivos, paginaArquivos, tamanhoPaginaArquivos]);

  function handleSalvarBase(evento) {
    evento.preventDefault();
    if (!chaveVectorStoreConfigurada) return;

    const nomeNormalizado = nome.trim();
    if (!nomeNormalizado || nomeNormalizado.length > 100) {
      setErroNome('Informe um nome de até 100 caracteres.');
      return;
    }

    setErroNome('');
    setErroGeral('');
    setSalvandoBase(true);
    const requisicao = editando
      ? caAPI.baseConhecimento.update(idBaseConhecimento, {
        nome: nomeNormalizado,
        ...orientacao,
        consulta_obrigatoria: consultaObrigatoria,
        ativo: baseAtiva,
      })
      : caAPI.baseConhecimento.create({ nome: nomeNormalizado });

    trackPromise(
      requisicao
        .then(() => {
          successToast.fire({ text: `Base de Conhecimento ${editando ? 'atualizada' : 'criada'} com sucesso.` });
          onSalvo();
        })
        .catch(erro => {
          if (erro?.response?.status === 422) {
            setErroNome(obterMensagemCampo(erro, 'nome', ''));
            setErrosOrientacao(CAMPOS_ORIENTACAO.reduce((acumulado, { campo }) => ({
              ...acumulado,
              [campo]: obterMensagemCampo(erro, campo, ''),
            }), {}));
            setErroGeral(obterMensagemCampo(erro, 'chave_vector_store', obterMensagemCampo(erro, 'id_projeto_openai', '')));
            return;
          }
          setErroGeral(obterMensagemErro(erro, `Erro ao ${editando ? 'atualizar' : 'criar'} Base de Conhecimento.`));
        })
        .finally(() => setSalvandoBase(false))
    );
  }

  function handleAlternarAprendizado(novoValor, confirmarRemocaoDestino = false) {
    const dados = { para_aprendizado: novoValor };
    if (confirmarRemocaoDestino) dados.confirmar_remocao_destino = true;

    return trackPromise(
      caAPI.baseConhecimento.marcarAprendizado(idBaseConhecimento, dados)
        .then(resposta => {
          const base = extrairDados(resposta) || {};
          const valorAtualizado = base.para_aprendizado !== undefined ? Boolean(base.para_aprendizado) : novoValor;
          setParaAprendizado(valorAtualizado);
          successToast.fire({ text: valorAtualizado ? 'Base marcada como de aprendizado.' : 'Base deixou de ser de aprendizado.' });
          // Atualiza o selo na listagem sem fechar o modal nem perder o que foi preenchido.
          if (onAtualizarLista) onAtualizarLista();
        })
        .catch(erro => {
          if (erro?.response?.status === 422) {
            const mensagemConfirmacao = obterMensagemCampo(erro, 'confirmar_remocao_destino', '');
            if (!novoValor && mensagemConfirmacao) {
              return Swal.fire({
                titleText: 'Confirmar alteração?',
                text: mensagemConfirmacao,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sim, continuar',
                cancelButtonText: 'Cancelar',
              }).then(resultado => {
                if (resultado.isConfirmed) return handleAlternarAprendizado(novoValor, true);
                setParaAprendizado(!novoValor);
              });
            }
            errorToast.fire({ text: obterMensagemCampo(erro, 'para_aprendizado', 'Não foi possível alterar a Base.') });
            setParaAprendizado(!novoValor);
            return;
          }
          errorToast.fire({ text: obterMensagemErro(erro, 'Erro ao alterar a Base de aprendizado.') });
          setParaAprendizado(!novoValor);
        })
    );
  }

  function handleAlterarTamanhoPaginaArquivos(novoTamanho) {
    setPaginaArquivos(1);
    setTamanhoPaginaArquivos(Number(novoTamanho) || 15);
  }

  function handleEnviarArquivos() {
    if (!chaveVectorStoreConfigurada || !arquivosSelecionados.length) return;

    const dados = new FormData();
    arquivosSelecionados.forEach(arquivo => dados.append('arquivos[]', arquivo));
    trackPromise(
      caAPI.baseConhecimento.enviarArquivos(idBaseConhecimento, dados)
        .then(resposta => {
          const resultado = extrairDados(resposta);
          if (resultado.enviados?.length) successToast.fire({ text: `${resultado.enviados.length} arquivo(s) enviado(s) com sucesso.` });
          if (resultado.duplicados?.length) infoToast.fire({ text: `${resultado.duplicados.length} arquivo(s) duplicado(s) não foram reenviados.` });
          (resultado.falhas || []).forEach(falha => errorToast.fire({ text: `${falha.nome}: ${falha.mensagem}` }));
          setArquivosSelecionados([]);
          setPaginaArquivos(1);
          return handleCarregarArquivos(1);
        })
        .catch(erro => {
          Swal.fire({
            titleText: 'Erro',
            text: obterMensagemErro(erro, 'Erro ao enviar arquivos.'),
            icon: 'error',
          });
          return false;
        }) 
    );
  }

  function handleRemoverArquivo(arquivo) {
    if (!chaveVectorStoreConfigurada) return;

    Swal.fire({
      titleText: 'Remover arquivo?',
      text: `Deseja remover o arquivo "${arquivo.nome}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar',
    }).then(resultado => {
      if (!resultado.isConfirmed) return;
      trackPromise(
        caAPI.baseConhecimento.removerArquivo(idBaseConhecimento, arquivo.id)
          .then(() => {
            successToast.fire({ text: 'Arquivo removido com sucesso.' });
            return handleCarregarArquivos(paginaArquivos);
          })
          .catch(erro => Swal.fire({
            titleText: 'Erro',
            text: obterMensagemErro(erro, 'Erro ao remover arquivo.'),
            icon: 'error',
          }))
      );
    });
  }

  const cabecalhoArquivos = (
    <tr>
      <th>Identificador</th>
      <th>Nome</th>
      <th>Origem</th>
      <th>Criado em</th>
      <th className="text-center">Ações</th>
    </tr>
  );

  const linhasArquivos = listaArquivos.map(arquivo => {
    const botaoRemoverArquivo = (
      <Button type="button" variant="outline-danger" title="Remover arquivo" aria-label={`Remover ${arquivo.nome}`} onClick={() => handleRemoverArquivo(arquivo)} disabled={!chaveVectorStoreConfigurada} style={!chaveVectorStoreConfigurada ? { pointerEvents: 'none' } : undefined}>
        <FontAwesomeIcon icon={faTrash} />
      </Button>
    );

    return (
      <tr key={arquivo.id}>
        <td>{arquivo.id}</td>
        <td>{arquivo.nome}</td>
        <td>
          {arquivo.origem_portal_corretor
            ? <span className="badge badge-info" title="Arquivo vinculado a partir do Portal do Corretor">Portal do Corretor</span>
            : <span className="badge badge-light" title="Arquivo enviado manualmente">Upload manual</span>}
        </td>
        <td>{arquivo.criado_em ? moment(arquivo.criado_em).format('DD/MM/YYYY HH:mm') : '-'}</td>
        <td className="text-center">
          <ButtonGroup size="sm">
            {chaveVectorStoreConfigurada ? botaoRemoverArquivo : (
              <OverlayTrigger overlay={tooltipChaveNaoConfigurada} placement="top">
                <span className="d-inline-block">{botaoRemoverArquivo}</span>
              </OverlayTrigger>
            )}
          </ButtonGroup>
        </td>
      </tr>
    );
  });

  return (
    <ModalDommus open={aberto} close={close} titulo={editando ? 'Editar Base de Conhecimento' : 'Nova Base de Conhecimento'} size="xl">
      <Form onSubmit={handleSalvarBase}>
        {erroGeral && <Alert variant="danger">{erroGeral}</Alert>}
        <Form.Group controlId="nome-base-conhecimento">
          <Form.Label>Nome</Form.Label>
          <Form.Control value={nome} maxLength={100} onChange={evento => setNome(evento.target.value)} isInvalid={Boolean(erroNome)} required />
          <Form.Control.Feedback type="invalid">{erroNome}</Form.Control.Feedback>
        </Form.Group>

        {editando && (
          <>
            <DommusToggle title="Orientação ao Agente" toggle>
              {CAMPOS_ORIENTACAO.map(({ campo, rotulo, ajuda }) => {
                const idCampo = `${campo.replace(/_/g, '-')}-base-conhecimento`;
                return (
                  <Form.Group controlId={idCampo} key={campo}>
                    <Form.Label>{rotulo}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      maxLength={MAXIMO_CARACTERES_ORIENTACAO}
                      value={orientacao[campo]}
                      onChange={evento => setOrientacao({ ...orientacao, [campo]: evento.target.value })}
                      isInvalid={Boolean(errosOrientacao[campo])}
                    />
                    <Form.Control.Feedback type="invalid">{errosOrientacao[campo]}</Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      {ajuda} ({orientacao[campo].length}/{MAXIMO_CARACTERES_ORIENTACAO})
                    </Form.Text>
                  </Form.Group>
                );
              })}
              <Form.Check
                type="switch"
                id="consulta-obrigatoria-base-conhecimento"
                label="Consulta obrigatória antes de responder sobre os assuntos desta base"
                checked={consultaObrigatoria}
                onChange={evento => setConsultaObrigatoria(evento.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="ativo-base-conhecimento"
                label="Base ativa (disponível para os Agentes)"
                checked={baseAtiva}
                onChange={evento => setBaseAtiva(evento.target.checked)}
                className="mb-3"
              />
              <Form.Check
                type="switch"
                id="para-aprendizado-base-conhecimento"
                label="Base de aprendizado (exclusiva para exemplos curados)"
                checked={paraAprendizado}
                onChange={evento => {
                  const novoValor = evento.target.checked;
                  setParaAprendizado(novoValor);
                  handleAlternarAprendizado(novoValor);
                }}
                className="mb-3"
              />
            </DommusToggle>
            {paraAprendizado && (
              <ExemplosAprendizado idBaseConhecimento={idBaseConhecimento} aberto={aberto} />
            )}
            <DommusToggle title="Subir Arquivos">
              <Form.Group controlId="arquivos-base-conhecimento">
                <Anexos
                  listaAnexos={arquivosSelecionados}
                  setListaAnexos={setArquivosSelecionados}
                  filesTypes={EXTENSOES_PERMITIDAS.map(extensao => `.${extensao}`)}
                  formData
                  maximoArquivos={MAXIMO_ARQUIVOS}
                  maximoTamanhoArquivoBytes={MAXIMO_ARQUIVO_BYTES}
                  maximoTamanhoLoteBytes={MAXIMO_LOTE_BYTES}
                  habilitarPreVisualizacaoImagem={false}
                  disabled={!chaveVectorStoreConfigurada}
                  clean={!arquivosSelecionados.length}
                />
                <Form.Text className="text-muted">Formatos permitidos: PDF, TXT, MD e DOCX. Máximo de 10 arquivos, 25 MB por arquivo e 100 MB por lote.</Form.Text>
              </Form.Group>
              {arquivosSelecionados.length > 0 && (
                <div className="mb-3">
                  <Button type="button" variant="primary" onClick={handleEnviarArquivos} disabled={!chaveVectorStoreConfigurada}>Enviar arquivos</Button>
                </div>
              )}
            </DommusToggle>
            {erroArquivos ? (
              <Alert variant="danger">{erroArquivos}</Alert>
            ) : listaArquivos.length ? (
              <DommusTabelaPaginadaControlada
                linhasCabecalho={cabecalhoArquivos}
                linhasCorpo={linhasArquivos}
                optionsTabela={{}}
                pagina={paginaArquivos}
                setPagina={setPaginaArquivos}
                tamanhoPagina={tamanhoPaginaArquivos}
                setTamanhoPagina={handleAlterarTamanhoPaginaArquivos}
                tamanhoTotal={totalArquivos}
              />
            ) : (
              <Alert variant="info">Nenhum arquivo cadastrado nesta Base.</Alert>
            )}
          </>
        )}

        <div className="d-flex justify-content-end">
          <Button type="button" variant="secondary" className="mr-2" onClick={close}>Cancelar</Button>
          {chaveVectorStoreConfigurada ? (
            <Button type="submit" variant="success" disabled={salvandoBase}>{salvandoBase ? 'Salvando...' : 'Salvar'}</Button>
          ) : (
            <OverlayTrigger overlay={tooltipChaveNaoConfigurada} placement="top">
              <span className="d-inline-block">
                <Button type="submit" variant="success" disabled style={{ pointerEvents: 'none' }}>Salvar</Button>
              </span>
            </OverlayTrigger>
          )}
        </div>
      </Form>
    </ModalDommus>
  );
}
